import React, { FC, useState } from 'react';
import { Customer, CustomerAttributes } from '@models';
import { LoadingButton } from '@mui/lab';
import { Dialog, DialogContent, Button, Grid, DialogTitle, IconButton, Typography, DialogActions, TextField } from '@mui/material';
import { useTerminateCustomerMutation } from '@services';
import { showToast } from '@store/slices/notification';
import { useAppDispatch } from '@hooks/redux-custom';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory } from 'react-router-dom';

type Props = {
  customer: Customer;
  isOpen: boolean;
  onClose?: () => void;
  bigCCustomerAttributes?: CustomerAttributes[] | undefined;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
};

export const TerminateAccountModal: FC<Props> = ({ isOpen, onClose = () => false, bigCCustomerAttributes, customer, setModal }: Props) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [reasonField, setReasonField] = useState({ reason: '', error: false });
  const [terminateAccount] = useTerminateCustomerMutation();
  const firebaseAttrId = bigCCustomerAttributes?.find((attr: CustomerAttributes) => attr.name === 'firebaseuid')?.id;
  const firebaseUid: string = customer.attributes.find((attribute) => attribute.attribute_id === firebaseAttrId)?.attribute_value || '';

  const onTerminateClick = () => {
    terminateAccount({ customerId: customer.id, firebaseUid, reason: reasonField.reason })
      .then((data) => {
        if (data?.error) {
          setModal(false);

          dispatch(
            showToast({
              message: 'Failed to terminate account',
              type: 'error',
            }),
          );
        } else {
          history.goBack();
          setModal(false);

          dispatch(
            showToast({
              message: 'User terminated successfully',
              type: 'success',
            }),
          );
        }
      })
      .catch((error) => {
        setModal(false);

        dispatch(
          showToast({
            message: 'Failed to terminate account',
            type: 'error',
          }),
        );
      });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        setReasonField({ reason: '', error: false });
        onClose();
      }}
      maxWidth="sm"
      PaperProps={{
        style: { borderRadius: 10 },
      }}
    >
      <DialogContent>
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <DialogTitle sx={{ fontSize: 30, m: 0, p: 2 }}>
            Terminate Customer Account
            {onClose && (
              <IconButton
                aria-label="close"
                onClick={() => {
                  setReasonField({ reason: '', error: false });
                  onClose();
                }}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
          <Grid item mb={1} xs={12}>
            <TextField
              error={reasonField.error}
              id="reason"
              label="Reason for termination"
              variant="outlined"
              fullWidth
              value={reasonField.reason}
              onChange={(event) => {
                setReasonField({ reason: event.target.value, error: false });
              }}
            />
          </Grid>
          {reasonField.error && (
            <Typography variant="body2" color="red">
              Reason field cannot be empty!
            </Typography>
          )}
          <Grid item mb={0.5} xs={12}>
            <Typography variant="body1">
              By clicking accept, you will be terminating this account, and the customer will no longer be able to access.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <DialogActions sx={{ justifyContent: 'center' }}>
              <Button
                sx={{
                  display: 'block',
                  marginRight: '10px',
                  backgroundColor: '#aaaaaa',
                  '&.MuiButtonBase-root:hover': {
                    bgcolor: '#aaaaaa',
                  },
                }}
                variant="contained"
                onClick={() => {
                  setReasonField({ reason: '', error: false });
                  onClose();
                }}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                onClick={() => {
                  if (!reasonField.reason.trim().length) {
                    setReasonField((prevState) => ({ ...prevState, error: true }));
                    return;
                  }

                  onTerminateClick();
                }}
                data-testid="accept-termination-button"
              >
                Accept
              </LoadingButton>
            </DialogActions>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
